import React from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { Icon } from "@/components/ui/Icon";
import { Feather } from "@expo/vector-icons";
import { Button } from "@/components/ui/Button";

// ─── Types ────────────────────────────────────────────────────────────────────

export type ReviewSection =
  | "legal_representative"
  | "business_information"
  | "product_compliance"
  | "financial_settlement"
  | "branding_localization";

export type FieldDecision = "approve" | "reject" | "edit";

export interface RejectFieldMeta {
  /** Translated field label shown in the "REJECTED FIELD" banner. */
  label: string;
  /** Text value of the field (e.g. field value string). */
  value?: string;
  /** S3 file key to preview in the banner (e.g. ID document or logo). */
  fileKey?: string;
}

export interface FieldRejectionData {
  reason: string;
  note?: string;
  rejectedAt: string;
  rejectedBy?: { name: string; role: string };
  /** Label of the rejected field (e.g. "Legal Representative ID"). Stored for the Show Reason modal. */
  fieldLabel?: string;
  /** Text value of the rejected field. Stored for the Show Reason modal. */
  fieldValue?: string;
  /** S3 file key of the rejected field (e.g. ID document). Stored for the Show Reason modal. */
  fieldFileKey?: string;
}

export interface EditFieldMeta {
  /** Translated field label displayed in the modal header. */
  label: string;
  /** The field's current value in the application (shown as read-only). */
  currentValue?: string;
  /** When true, renders AppTextArea inputs instead of AppTextInput. */
  multiline?: boolean;
}

export interface FieldEditData {
  /** The new value entered by the reviewer. Shown as "Current Value" in the show-edit modal. */
  newValue: string;
  /** The original application value before the edit. Shown as "Previous Value". */
  previousValue?: string;
  /** Stored field label — needed to reconstruct EditFieldMeta when "Update" is pressed. */
  fieldLabel: string;
  /** Whether the field uses a multiline textarea — preserved for re-edit. */
  multiline?: boolean;
}

// ─── Shared sub-components ────────────────────────────────────────────────────

/** A "VIEW ONLY" badge used to mark read-only fields in review mode. */
export function ViewOnlyBadge({ label }: { label: string }) {
  return (
    <View className="bg-stroke-40 rounded px-2 py-0.5">
      <AppText weight="semibold" className="text-[11px] text-dark-text-10 tracking-[0.44px]">
        {label}
      </AppText>
    </View>
  );
}

export interface ReviewFieldProps {
  label: string;
  viewOnlyLabel: string;
  children: React.ReactNode;
  /** When true the label row does NOT show the VIEW ONLY badge (e.g., document-type label). */
  noViewOnly?: boolean;
}

export function ReviewField({ label, viewOnlyLabel, children, noViewOnly }: ReviewFieldProps) {
  return (
    <View className="gap-4 flex-1 items-start w-full">
      <View className="flex-row items-center gap-1 flex-wrap">
        <AppText weight="semibold" className="text-base text-supporting-text-40">
          {label}
        </AppText>
        {!noViewOnly && <ViewOnlyBadge label={viewOnlyLabel} />}
      </View>
      {children}
    </View>
  );
}

export interface FieldRowProps {
  children: React.ReactNode;
}

export function FieldRow({ children }: FieldRowProps) {
  return <View className="flex-col tablet:flex-row gap-10 w-full">{children}</View>;
}

export function Divider() {
  return <View className="h-px w-full bg-stroke-40 my-8" />;
}

// ─── Post-decision field state buttons ───────────────────────────────────────

interface FieldDecisionActionsProps {
  fieldKey: string;
  decision: FieldDecision;
  onUndoDecision: (fieldKey: string) => void;
  onShowReason: (fieldKey: string) => void;
  onShowEdit?: (fieldKey: string) => void;
  t: ReturnType<typeof import("@/context/I18nContext").useI18n>["t"];
}

/**
 * Renders the post-decision button pair for a field.
 * Replaces ContentActions once a field has been approved, rejected, or edited.
 */
export function FieldDecisionActions({
  fieldKey,
  decision,
  onUndoDecision,
  onShowReason,
  onShowEdit,
  t,
}: FieldDecisionActionsProps) {
  if (decision === "reject") {
    return (
      <View className="flex-row flex-wrap gap-3 items-center">
        {/* Non-interactive "Rejected" status indicator */}
        <Button
          label="review.review.fieldState.rejected"
          onPress={() => {}}
          variant="destructive"
          activeOpacity={1}
        />
        {/* "Show Reason" opens the field-level rejection reason modal */}
        <Button
          label="review.review.fieldState.showReason"
          onPress={() => onShowReason(fieldKey)}
          variant="secondary"
          labelClassName="text-supporting-text"
          icon={<Icon as={Feather} name="eye" size={14} className="text-supporting-text" />}
        />
        {/* "Undo Decision" clears the rejection */}
        <Button
          label="review.review.fieldState.undoDecision"
          onPress={() => onUndoDecision(fieldKey)}
          variant="outline"
        />
      </View>
    );
  }

  if (decision === "approve") {
    return (
      <View className="flex-row flex-wrap gap-3 items-center">
        {/* Non-interactive "Approved" status indicator */}
        <Button
          label="review.review.fieldState.approved"
          onPress={() => {}}
          variant="success"
          activeOpacity={1}
        />
        {/* "Undo Decision" clears the approval */}
        <Button
          label="review.review.fieldState.undoDecision"
          onPress={() => onUndoDecision(fieldKey)}
          variant="outline"
        />
      </View>
    );
  }

  if (decision === "edit") {
    return (
      <View className="flex-row flex-wrap gap-3 items-center">
        {/* Non-interactive "Edited" status indicator */}
        <Button
          label="review.review.fieldState.edited"
          onPress={() => {}}
          variant="blue"
          activeOpacity={1}
        />
        {/* "Show Edit" — stub for now, will show edit comparison in future */}
        <Button
          label="review.review.fieldState.showEdit"
          onPress={() => onShowEdit?.(fieldKey)}
          variant="secondary"
          labelClassName="text-supporting-text"
          icon={<Icon as={Feather} name="eye" size={14} className="text-supporting-text" />}
        />
      </View>
    );
  }

  return null;
}
